import { useEffect, useState } from "react";
import { TrendingUp, Calculator, Info } from "lucide-react";
import api from "@/lib/api";

const eur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0, useGrouping: "always" }).format(n || 0);

/** Rentabilidad media real de las farmacias que ya usan FarmaClass.
 *  El dato se calcula en vivo sobre los estudios de la cartera (anónimo). */
export const RoiSection = ({ onCta }) => {
  const [roi, setRoi] = useState(null);
  const [monthly, setMonthly] = useState(40000);

  useEffect(() => {
    api.get("/public/roi").then(({ data }) => data.available && setRoi(data)).catch(() => {});
  }, []);

  if (!roi) return null;

  const pp = roi.avg_delta_eur_pp || 0;
  const myBenefit = Math.round(monthly * 12 * (pp / 100) * (roi.margin_pct / 100));

  return (
    <section
      className="relative bg-slate-950 text-white overflow-hidden border-y border-emerald-500/20"
      data-testid="landing-roi-section"
    >
      <div className="absolute inset-0 opacity-[0.18] bg-[radial-gradient(circle_at_80%_20%,#34d399,transparent_45%),radial-gradient(circle_at_10%_90%,#0ea5e9,transparent_40%)]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-300 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-3 py-1.5">
            <TrendingUp className="h-3.5 w-3.5" /> Rentabilidad de la inversión
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-5 leading-[1.08]">
            Lo que gana de media
            <span className="block text-emerald-300">una farmacia con FarmaClass.</span>
          </h2>
          <p className="text-base md:text-lg text-slate-300 mt-5 leading-relaxed">
            No es una estimación de folleto: son los datos reales de{" "}
            <strong className="text-white">
              {roi.pharmacies === 1 ? "la farmacia" : `las ${roi.pharmacies} farmacias`}
            </strong>{" "}
            que ya {roi.pharmacies === 1 ? "trabaja" : "trabajan"} con nosotros, calculados sobre sus propios
            estudios de dispensación.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-12">
          <div className="sm:col-span-2 rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.07] p-7 sm:p-9" data-testid="roi-main-card">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-300">
              Beneficio medio al año
            </div>
            <div className="font-display font-extrabold font-mono-num text-5xl sm:text-6xl lg:text-7xl text-emerald-300 mt-3 leading-none tabular-nums">
              {eur(roi.avg_benefit_year)}
            </div>
            <div className="text-sm text-slate-300 mt-4">
              Unos <strong className="text-white font-mono-num">{eur(roi.avg_benefit_month)}</strong> más cada mes,
              solo por dispensar mejor lo que ya dispensas.
            </div>
          </div>
          <div className="grid grid-rows-2 gap-4 sm:gap-6">
            <Stat
              label="Mejora media de cuota"
              value={`+${(roi.avg_delta_eur_pp || 0).toLocaleString("es-ES")} pts`}
              sub="de genérico sobre su compra"
              testid="roi-stat-pp"
            />
            <Stat
              label="La mejor farmacia"
              value={eur(roi.best_benefit_year)}
              sub="de beneficio al año"
              testid="roi-stat-best"
            />
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/15 bg-white/[0.04] p-7 sm:p-9" data-testid="roi-calculator">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-300">
            <Calculator className="h-3.5 w-3.5" /> Calcula el tuyo
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-5 items-center">
            <div>
              <label className="block text-sm text-slate-300" htmlFor="roi-monthly">
                ¿Cuánto compras al mes en medicamento de receta?
              </label>
              <div className="flex items-baseline gap-2 mt-3">
                <input
                  id="roi-monthly"
                  type="range"
                  min="10000"
                  max="150000"
                  step="5000"
                  value={monthly}
                  onChange={(e) => setMonthly(Number(e.target.value))}
                  className="flex-1 accent-emerald-400"
                  data-testid="roi-monthly-input"
                />
                <span className="font-display font-extrabold font-mono-num text-xl text-white w-32 text-right tabular-nums">
                  {eur(monthly)}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                Aplicamos tu mejora esperada ({(roi.avg_delta_eur_pp || 0).toLocaleString("es-ES")} puntos, la media
                conseguida) y un margen adicional del {roi.margin_pct}% del genérico.
              </p>
            </div>
            <div className="lg:border-l lg:border-white/10 lg:pl-8">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">
                Ganarías al año
              </div>
              <div
                className="font-display font-extrabold font-mono-num text-4xl sm:text-5xl text-white mt-2 leading-none tabular-nums"
                data-testid="roi-my-benefit"
              >
                {eur(myBenefit)}
              </div>
              <button
                onClick={onCta}
                className="mt-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-6 py-3.5 rounded-xl font-bold transition-colors"
                data-testid="roi-cta"
              >
                Quiero mi análisis
              </button>
            </div>
          </div>
        </div>

        <p className="flex items-start gap-2 text-xs text-slate-400 mt-6 max-w-3xl leading-relaxed">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Cálculo sobre el importe de compra que pasa de marca a genérico en cada farmacia, con un margen adicional
          medio del {roi.margin_pct}%. Datos anónimos y agregados, actualizados automáticamente cada mes.
        </p>
      </div>
    </section>
  );
};

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-2xl border border-white/15 bg-white/[0.04] p-6 flex flex-col justify-center" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">{label}</div>
    <div className="font-display font-extrabold font-mono-num text-2xl sm:text-3xl text-white mt-2 tabular-nums">{value}</div>
    <div className="text-xs text-slate-400 mt-1">{sub}</div>
  </div>
);
